import { useCallback, useState } from 'react'

import { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { useAccount, useSignMessage } from 'wagmi'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { invalidatePositionBalancesQuery } from '~/components/smart-accounts/positions/hooks/usePositionBalances'
import { invalidateVestingBalancesQuery } from '~/components/smart-accounts/vesting/hooks/useVestingBalances'
import { MUTATION_KEYS, QUERY_KEYS } from '~/constants/react-query'
import { useWalletConnectContext } from '~/context/WalletConnectProvider'
import { useActiveOrgId } from '~/hooks/org'
import { useHandleInsufficientNativeTokenError } from '~/hooks/transactions/useHandleInsufficientNativeTokenError'
import { useConnectedAddress } from '~/hooks/useConnectedAddress'
import { useCopyValue } from '~/hooks/useCopyValue'
import { useServerRequests } from '~/hooks/useRequests'
import { useSubmitTransactionBatch } from '~/hooks/useTransactionBatch'
import { useUpdateGasEstimation } from '~/hooks/useTransactions'
import {
  BackendSmartAccount,
  BackendTransaction,
  BasicTransactionType,
  OrgTransaction,
  TransactionStatus,
} from '~/types'
import { InfiniteTransactionsResponse } from '~/types/query-types'
import { Address } from '~/utils/address'
import { captureException } from '~/utils/errors'
import {
  getPasskeyErrorDescription,
  startPasskeyAuthentication,
} from '~/utils/passkeys'
import { convertChallengeToOriginalHash } from '~/utils/smartAccounts'
import { isDraftBatchTransaction } from '~/utils/smartAccounts/batch'
import { checkEoaSignature } from '~/utils/smartAccounts/eoaSignature'
import { showEoaSignatureRejectedToast } from '~/utils/smartAccounts/eoaSignatureToast'
import {
  checkEoaSigningReadiness,
  EoaSigningReadinessFailureReason,
} from '~/utils/smartAccounts/signingReadiness'
import {
  getTransactionUrl,
  verifyAuthOptions,
} from '~/utils/smartAccounts/transactions'
import { sendTrackingData } from '~/utils/tracking'

export type ApproveAndSubmitTransactionParams = {
  transaction: BackendTransaction
  smartAccount: BackendSmartAccount
  onSign?: (status: TransactionStatus) => void
  onSubmit?: (userOpHash: string) => void
  onConfirmation?: (status: TransactionStatus) => void
  onSubmitError?: (e: unknown) => void
}

export type PollSubmittedTransactionParams = {
  transactionId: string
  onSuccess: (transaction: OrgTransaction) => void
  onFailure: (transaction: OrgTransaction) => void
}

export const usePollSubmittedTransaction = (): {
  pollTransaction: (params: PollSubmittedTransactionParams) => Promise<void>
} => {
  const { respondWithTransactionHash } = useWalletConnectContext()
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  const MAX_POLL_ATTEMPTS = 120 // 60 seconds at 500ms intervals

  const pollTransaction = useCallback(
    async (
      transactionId: string,
      onSuccess: (transaction: OrgTransaction) => void,
      onFailure: (transaction: OrgTransaction) => void,
    ): Promise<void> => {
      let attempt = 0

      const poll = async (): Promise<void> => {
        if (attempt >= MAX_POLL_ATTEMPTS) {
          toast.error('Transaction confirmation timed out', {
            id: transactionId,
            description:
              'The transaction may still be processing. Please check back later.',
          })
          return
        }

        const result = await serverGetRequest<{
          transaction: OrgTransaction
        }>({
          path: `/api/smart_accounts/transactions/${transactionId}`,
        })

        if (
          result &&
          result.data.transaction.status === TransactionStatus.Succeeded
        ) {
          if (
            result.data.transaction.walletConnectSessionRequestId &&
            result.data.transaction.walletConnectSessionRequestTopic
          )
            respondWithTransactionHash(result.data.transaction)
          onSuccess(result.data.transaction)
          return
        } else if (
          result &&
          result.data.transaction.status === TransactionStatus.Failed
        ) {
          onFailure(result.data.transaction)
          return
        } else if (
          result &&
          result.data.transaction.status === TransactionStatus.Submitted
        ) {
          // Only continue polling if transaction is submitted and awaiting confirmation
          attempt++
          await new Promise((resolve) => setTimeout(resolve, 500))
          // Recursive tail call — surface any rejected attempts to Sentry so we
          // don't leak an unhandled rejection when the caller fire-and-forgets.
          return poll().catch((e) => captureException(e))
        }
        // Exit without polling for other statuses (CREATED, QUEUED, APPROVED, etc.)
        // This prevents infinite polling when user dismisses passkey before submission
      }

      return poll()
    },
    [respondWithTransactionHash, serverGetRequest],
  )

  const { mutateAsync: pollTransactionMutation } = useMutation({
    mutationKey: [
      QUERY_KEYS.TRANSACTION,
      MUTATION_KEYS.TRANSACTION_CONFIRM,
      activeOrgId,
    ],
    mutationFn: async (params: PollSubmittedTransactionParams) => {
      return await pollTransaction(
        params.transactionId,
        params.onSuccess,
        params.onFailure,
      )
    },
  })

  return { pollTransaction: pollTransactionMutation }
}

export type SubmitTransactionParams = {
  transaction: BackendTransaction
  onSubmit?: (userOpHash: string) => void
  onConfirmation?: (status: TransactionStatus) => void
  onError?: (e: unknown) => void
}

/**
 * Shared cache/toast helpers for handling poll results after a transaction is submitted.
 * Used by both useSubmitTransaction (APPROVED → submit → poll) and the SUBMITTED
 * branch in useApproveAndSubmitTransaction (backend already submitted → poll).
 */
const useTransactionConfirmationHandlers = () => {
  const { handleCopy } = useCopyValue()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const invalidateTransactionQueries = useCallback(
    (transactionId: string) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transactionId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey[1]?.orgId === activeOrgId
          )
        },
      })
    },
    [queryClient, activeOrgId],
  )

  const setTransactionQueryData = useCallback(
    (transaction: BackendTransaction) => {
      queryClient.setQueryData(
        [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
        transaction,
      )
      queryClient.setQueriesData<InfiniteData<InfiniteTransactionsResponse>>(
        { queryKey: [QUERY_KEYS.TRANSACTIONS] },
        (oldData) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              transactions: page.transactions.map((t) => {
                if (t.isSplitsTransaction && t.id === transaction.id) {
                  return { ...t, status: transaction.status }
                }
                return t
              }),
            })),
          }
        },
      )
    },
    [queryClient, activeOrgId],
  )

  const makePollHandlers = useCallback(
    (
      transaction: BackendTransaction,
      onConfirmation?: (status: TransactionStatus) => void,
    ) => ({
      onSuccess: (updatedTransaction: OrgTransaction) => {
        setTransactionQueryData({
          ...transaction,
          ...updatedTransaction,
        })
        invalidateTransactionQueries(transaction.id)

        toast.success('Transaction succeeded', {
          id: transaction.id,
          description: <TransactionTitle transaction={transaction} unstyled />,
          action: {
            label: 'Copy receipt link',
            onClick: () => handleCopy(getTransactionUrl(transaction)),
          },
        })

        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.SMART_ACCOUNT_BALANCES,
            transaction.smartAccountAddress,
          ],
        })
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORG_BALANCES, activeOrgId],
        })
        if (
          !transaction.isMerkleTransaction &&
          transaction.types.includes(BasicTransactionType.Claim)
        ) {
          invalidateVestingBalancesQuery(
            queryClient,
            activeOrgId,
            transaction.smartAccountId,
          )
          invalidatePositionBalancesQuery(
            queryClient,
            activeOrgId,
            transaction.smartAccountId,
          )
        }
        onConfirmation?.(TransactionStatus.Succeeded)
      },
      onFailure: (updatedTransaction: OrgTransaction) => {
        setTransactionQueryData({
          ...transaction,
          ...updatedTransaction,
        })
        invalidateTransactionQueries(transaction.id)

        toast.error('Transaction failed', {
          id: transaction.id,
          description: updatedTransaction.revertReason ?? undefined,
        })

        onConfirmation?.(TransactionStatus.Failed)
      },
    }),
    [
      setTransactionQueryData,
      invalidateTransactionQueries,
      queryClient,
      activeOrgId,
      handleCopy,
    ],
  )

  return {
    invalidateTransactionQueries,
    setTransactionQueryData,
    makePollHandlers,
  }
}

const useSubmitTransaction = (): {
  submitTransaction: (submitTxn: SubmitTransactionParams) => Promise<void>
  isProcessing: boolean
} => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const { pollTransaction } = usePollSubmittedTransaction()
  const activeOrgId = useActiveOrgId()
  const pathname = usePathname()

  const handleInsufficientNativeTokenError =
    useHandleInsufficientNativeTokenError()

  const { invalidateTransactionQueries, makePollHandlers } =
    useTransactionConfirmationHandlers()

  const submitTransaction = useCallback(
    async ({
      transaction,
      onSubmit,
      onError,
      onConfirmation,
    }: {
      transaction: BackendTransaction
      onSubmit?: (userOpHash: string) => void
      onError?: (e: unknown) => void
      onConfirmation?: (status: TransactionStatus) => void
    }) => {
      const isOnTransactionPage = pathname.includes(
        `/transactions/${transaction.id}`,
      )
      if (!isOnTransactionPage && transaction.isPublicTransaction) {
        throw new Error(
          'Should never be viewing a public transaction outside of the transaction page',
        )
      }

      const result = await serverPutRequest<{
        userOpHash: string
        transactionStatus: TransactionStatus
      }>({
        path: `/api/smart_accounts/transactions/${transaction.id}/submit`,
        params: {
          smartAccountId: transaction.smartAccountId,
        },
        errorToastId: transaction.id,
        errorMessage: 'Failed to submit transaction',
        overrideErrorMessageOptions: handleInsufficientNativeTokenError,
        onError,
      })

      // Refetch the transaction on success and failure (in case transaction was reverted)
      invalidateTransactionQueries(transaction.id)

      if (result) {
        toast.success('Transaction submitted...', {
          id: transaction.id,
          description: <TransactionTitle transaction={transaction} unstyled />,
        })

        onSubmit?.(result.data.userOpHash)

        toast.loading('Confirming transaction...', {
          id: transaction.id,
          description: <TransactionTitle transaction={transaction} unstyled />,
        })

        const { onSuccess, onFailure } = makePollHandlers(
          transaction,
          onConfirmation,
        )

        void pollTransaction({
          transactionId: transaction.id,
          onSuccess,
          onFailure,
        }).catch((e) => captureException(e))
      }
    },
    [
      pathname,
      serverPutRequest,
      invalidateTransactionQueries,
      makePollHandlers,
      pollTransaction,
      handleInsufficientNativeTokenError,
    ],
  )

  const { mutateAsync: submitTransactionMutation } = useMutation({
    mutationKey: [
      QUERY_KEYS.TRANSACTION,
      MUTATION_KEYS.TRANSACTION_SUBMIT,
      activeOrgId,
    ],
    mutationFn: submitTransaction,
  })

  return {
    submitTransaction: submitTransactionMutation,
    isProcessing,
  }
}

export type ApproveTransactionParams = {
  transaction: BackendTransaction
  smartAccount: BackendSmartAccount
  options: {
    // showSuccessToast: whether to trigger a success toast or a normal one on sucessful signature verification
    // Pass true if this is the terminating step, false if it's an intermediate step
    showSuccessToast: boolean
    onSuccess?: (status: TransactionStatus) => void
  }
}

/**
 * Emits the toast + captureException pair for each EOA signing readiness
 * failure surfaced by `checkEoaSigningReadiness`. Behavior is the same as
 * the prior inline guard; metadata shape is normalized so every branch
 * carries `{ tags: { feature: 'email-signer' }, extra: {...} }`.
 */
const handleReadinessFailure = ({
  reason,
  transaction,
  smartAccount,
  signingAddress,
}: {
  reason: EoaSigningReadinessFailureReason
  transaction: BackendTransaction
  smartAccount: BackendSmartAccount
  signingAddress: string | undefined
}) => {
  const baseExtra = {
    transactionId: transaction.id,
    smartAccountId: smartAccount.id,
    connectedAddress: signingAddress,
    expectedAddresses: smartAccount.eoaSigners.map((s) => s.address),
  }

  if (reason === 'already-signed') {
    toast.error('Already signed', {
      description: 'This signer has already approved this transaction.',
      id: transaction.id,
    })
    return
  }

  if (reason === 'not-a-signer') {
    captureException(
      new Error(
        `Connected address is not a signer for smart account ${smartAccount.id}`,
      ),
      {
        tags: { feature: 'email-signer' },
        extra: baseExtra,
      },
    )
    toast.error('Connected wallet is not a signer', {
      description:
        'Please sign out and log in with the email used to set up this signer.',
      id: transaction.id,
    })
    return
  }

  // reason === 'wallet-not-ready': no connected address yet — wagmi is still
  // activating the embedded wallet. User can retry in a moment.
  captureException(
    new Error(
      `EOA signing unavailable: wallet not activated for smart account ${smartAccount.id}`,
    ),
    {
      tags: { feature: 'email-signer' },
      extra: baseExtra,
    },
  )
  toast.error('Wallet not ready', {
    description:
      'Please wait for your wallet to finish activating and try again.',
    id: transaction.id,
  })
}

const useApproveTransaction = (): {
  approveTransaction: (
    params: ApproveTransactionParams,
  ) => Promise<TransactionStatus | undefined>
  isProcessing: boolean
} => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()
  const connectedAddress = useConnectedAddress()
  const { signMessageAsync } = useSignMessage()
  const { connector } = useAccount()
  const activeOrgId = useActiveOrgId()

  const invalidateTransactionQuery = useCallback(
    (transaction: BackendTransaction) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey[1]?.smartAccountId === transaction.smartAccountId
          )
        },
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
      })
    },
    [queryClient, activeOrgId],
  )

  const approveTransaction = useCallback(
    async (
      transaction: BackendTransaction,
      smartAccount: BackendSmartAccount,
      options: {
        showSuccessToast: boolean
        onSuccess?: (status: TransactionStatus) => void
      },
    ) => {
      const { showSuccessToast, onSuccess } = options
      // Pin the address we intend to sign with for the entirety of this call so
      // later guards, signMessageAsync, and the backend verify payload all refer
      // to the same value. wagmi's active account can flip mid-flight (Privy
      // logout, wallet swap); the catch in approveAndSubmitTransaction surfaces
      // the resulting ConnectorNotConnectedError to the user.
      const signingAddress = connectedAddress
      if (transaction.status === 'CREATED' || transaction.status === 'QUEUED') {
        const numSignatures =
          transaction.signers.length + transaction.eoaSigners.length
        const hasPasskeySigners = smartAccount.signers.length > 0
        const hasEoaSigners = smartAccount.eoaSigners.length > 0
        const readiness = checkEoaSigningReadiness({
          connectedAddress: signingAddress,
          smartAccount,
          transaction,
        })
        const useEOASigning = readiness.ok

        // Don't silently fall through to the passkey path when the smart account
        // has no passkey signers — this produces a misleading "No passkey signers
        // configured" toast for email-only recovery accounts whenever wagmi hasn't
        // finished activating the Privy embedded wallet. Surface the specific
        // reason EOA signing was declined so the caller can correct it.
        if (!readiness.ok && hasEoaSigners && !hasPasskeySigners) {
          handleReadinessFailure({
            reason: readiness.reason,
            transaction,
            smartAccount,
            signingAddress,
          })
          return
        }

        const submit = transaction.isPublicTransaction

        let attResp, verificationParams

        const options = JSON.parse(
          transaction.options,
        ) as PublicKeyCredentialRequestOptionsJSON

        if (
          !verifyAuthOptions({
            options,
            transaction,
            isLastSigner: numSignatures === smartAccount.threshold - 1,
          })
        ) {
          captureException(
            new Error(
              `Failed to verify authentication options for ${transaction.id}`,
            ),
          )
          toast.error('Failed to sign transaction', {
            description: 'Please contact support@splits.org',
            id: transaction.id,
          })
          return
        }

        // The server uses a 15 second cache for gas estimations, make sure this
        // number is never smaller than that one.
        const SIGNING_TIMEOUT_MS = 90_000
        const signingStartMs = Date.now()

        if (useEOASigning) {
          // `checkEoaSigningReadiness` above guarantees a non-empty
          // signingAddress when `readiness.ok` is true. The narrowing is
          // preserved locally by re-asserting to keep TS happy without
          // widening the module's type surface.
          if (!signingAddress) return
          const signingHash = convertChallengeToOriginalHash(options.challenge)
          attResp = await signMessageAsync({
            account: signingAddress,
            message: { raw: signingHash },
          })

          const signatureCheck = await checkEoaSignature({
            hash: signingHash,
            signature: attResp,
            expectedAddress: signingAddress,
          })
          if (signatureCheck !== 'valid') {
            captureException(new Error(`EOA signature ${signatureCheck}`), {
              tags: { feature: 'eoa-signer', operation: 'transaction_signing' },
              extra: {
                transactionId: transaction.id,
                smartAccountId: smartAccount.id,
                connector: connector?.id,
                signingAddress,
              },
            })
            showEoaSignatureRejectedToast({
              check: signatureCheck,
              toastId: transaction.id,
            })
            return
          }

          verificationParams = {
            path: `/api/smart_accounts/transactions/${transaction.id}/sign_eoa`,
            params: {
              authentication: attResp,
              signerNonce: numSignatures + 1,
              eoaSigner: signingAddress,
              submit,
            },
          }
        } else {
          // Guard against attempting passkey authentication when no passkey signers exist
          if (smartAccount.signers.length === 0) {
            captureException(
              new Error(
                `Attempted passkey authentication with no passkey signers for smart account ${smartAccount.id}`,
              ),
            )
            toast.error('Unable to sign transaction', {
              description: 'No passkey signers configured for this account',
              id: transaction.id,
            })
            return
          }

          attResp = await startPasskeyAuthentication(options, {
            toastId: transaction.id,
            onError: ({ error, isNotAllowedError, isUnsupportedError }) => {
              if (isUnsupportedError) {
                toast.error('Browser does not support passkeys', {
                  description:
                    error instanceof Error ? error.message : String(error),
                  id: transaction.id,
                })
                return
              }
              if (isNotAllowedError) {
                toast.dismiss(transaction.id)
                return
              }
              toast.error('Failed to sign transaction', {
                description: getPasskeyErrorDescription(error),
                id: transaction.id,
              })
              captureException(error, {
                tags: {
                  feature: 'passkey',
                  operation: 'transaction_signing',
                  errorType: error instanceof Error ? error.name : 'unknown',
                },
                extra: {
                  errorMessage:
                    error instanceof Error ? error.message : String(error),
                  transactionId: transaction.id,
                  smartAccountId: transaction.smartAccountId,
                  chainId: transaction.chainId,
                  transactionTypes: transaction.types,
                  isWebAuthnError:
                    error instanceof Error && error.name?.includes('Error'),
                },
              })
            },
          })

          if (!attResp) return

          verificationParams = {
            path: `/api/smart_accounts/transactions/${transaction.id}/sign`,
            params: {
              authentication: JSON.stringify(attResp),
              signerNonce: numSignatures + 1,
              submit,
            },
          }
        }

        const signingDurationMs = Date.now() - signingStartMs
        const isValid = signingDurationMs < SIGNING_TIMEOUT_MS
        const signerType = useEOASigning ? 'eoa' : 'passkey'
        const isPrivyEmailSigner =
          useEOASigning &&
          !!signingAddress &&
          smartAccount.eoaSigners.some(
            (s) => !!s.email && Address.equal(s.address, signingAddress),
          )
        sendTrackingData('clientTxSigned', {
          transactionId: transaction.id,
          smartAccountId: transaction.smartAccountId,
          orgId: smartAccount.orgId,
          chainId: transaction.chainId,
          transactionTypes: transaction.types,
          signingDurationMs,
          isValid,
          signerType,
          isPrivyEmailSigner,
        })
        if (!isValid) {
          toast.error(
            `Signing timed out (${Math.round(SIGNING_TIMEOUT_MS / 1000)}s)`,
            {
              description: 'Please try again',
              id: transaction.id,
            },
          )
          return
        }

        const verificationResponse = await serverPutRequest<{
          verified: boolean
          transaction: {
            status: TransactionStatus
            options: string
            signers: OrgTransaction['signers']
          }
        }>({
          ...verificationParams,
          onError: () => {
            invalidateTransactionQuery(transaction)
          },
          errorToastId: transaction.id,
          errorMessage: 'Failed to sign transaction',
        })

        if (verificationResponse && verificationResponse.data.verified) {
          const toastMethod = showSuccessToast ? toast.success : toast.loading

          toastMethod(
            showSuccessToast ? 'Transaction signed' : 'Transaction signed...',
            {
              id: transaction.id,
              description: (
                <TransactionTitle transaction={transaction} unstyled />
              ),
            },
          )

          invalidateTransactionQuery(transaction)
          onSuccess?.(verificationResponse.data.transaction.status)
          return verificationResponse.data.transaction.status
        }
      }
    },
    [
      serverPutRequest,
      invalidateTransactionQuery,
      signMessageAsync,
      connectedAddress,
      connector,
    ],
  )

  const { mutateAsync: approveTransactionMutation } = useMutation({
    mutationKey: [
      QUERY_KEYS.TRANSACTION,
      MUTATION_KEYS.TRANSACTION_SIGN,
      activeOrgId,
    ],
    mutationFn: async (params: ApproveTransactionParams) => {
      return await approveTransaction(
        params.transaction,
        params.smartAccount,
        params.options,
      )
    },
  })

  return {
    approveTransaction: approveTransactionMutation,
    isProcessing,
  }
}

export const useApproveAndSubmitTransaction = (): {
  approveAndSubmitTransaction: (
    params: ApproveAndSubmitTransactionParams,
  ) => Promise<void>
  isProcessing: boolean
} => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { approveTransaction } = useApproveTransaction()
  const { submitTransaction } = useSubmitTransaction()
  const { submitBatch } = useSubmitTransactionBatch()
  const { pollTransaction } = usePollSubmittedTransaction()
  const queryClient = useQueryClient()
  const updateGasEstimation = useUpdateGasEstimation()
  const activeOrgId = useActiveOrgId()
  const { makePollHandlers } = useTransactionConfirmationHandlers()

  const approveAndSubmitTransaction = useCallback(
    async ({
      transaction: inputTransaction,
      smartAccount,
      onSign,
      onSubmit,
      onConfirmation,
      onSubmitError,
    }: ApproveAndSubmitTransactionParams) => {
      setIsProcessing(true)
      try {
        // Drafts must be submitted before they can be signed/executed.
        const submittedTransaction = isDraftBatchTransaction(inputTransaction)
          ? await submitBatch({
              smartAccountId: inputTransaction.smartAccountId,
            })
          : inputTransaction
        if (!submittedTransaction) {
          setIsProcessing(false)
          return
        }

        const updatedTransaction = await updateGasEstimation({
          transactionId: submittedTransaction.id,
        })

        // Error toast handled inside useUpdateGasEstimation.
        if (!updatedTransaction) {
          setIsProcessing(false)
          return
        }

        const transaction =
          updatedTransaction.transaction ?? submittedTransaction

        let status = transaction.status
        if (status === 'CREATED' || status === 'QUEUED') {
          const isOnlySigning =
            status === 'CREATED' && smartAccount.threshold > 1
          const showSuccessToast = isOnlySigning
          const newStatus = await approveTransaction({
            transaction,
            smartAccount,
            options: {
              showSuccessToast,
              onSuccess: onSign,
            },
          })
          if (newStatus) status = newStatus
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
          })
        }
        if (status === 'APPROVED') {
          await submitTransaction({
            transaction,
            onSubmit,
            onConfirmation: () => {
              onConfirmation?.(status)
            },
            onError: onSubmitError,
          })
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.TRANSACTION, activeOrgId, transaction.id],
          })
        }
        // Backend already submitted (e.g. public transaction auto-submit after sign_eoa)
        if (status === 'SUBMITTED') {
          toast.loading('Confirming transaction...', {
            id: transaction.id,
            description: (
              <TransactionTitle transaction={transaction} unstyled />
            ),
          })
          const { onSuccess, onFailure } = makePollHandlers(
            transaction,
            onConfirmation,
          )
          void pollTransaction({
            transactionId: transaction.id,
            onSuccess,
            onFailure,
          }).catch((e) => captureException(e))
        }
      } catch (e) {
        captureException(e)
        // Replace the loading toast so the user isn't stuck on a frozen spinner
        // when sign/submit fails (e.g. wallet disconnected mid-sign, network blip).
        toast.error('Failed to sign transaction', {
          id: inputTransaction.id,
          description: 'Please try again',
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [
      approveTransaction,
      submitTransaction,
      submitBatch,
      pollTransaction,
      queryClient,
      updateGasEstimation,
      activeOrgId,
      makePollHandlers,
    ],
  )

  return {
    approveAndSubmitTransaction,
    isProcessing,
  }
}
